import React from 'react';
import styled from 'styled-components';

import {Link, pxToEm} from '../components/globalStyles';
import SectionDescription from '../components/sectionDescription';
import {AppRoutePathFunction} from '../logic/routes';
import ExceptionalViewerExperienceSvg from '../media/hexagonIconImage/icons/exceptional_viewer_experience.svg';
import FasterTimeToMarketSvg from '../media/hexagonIconImage/icons/faster_time_to_market.svg';
import OperationalOptimizationSvg from '../media/hexagonIconImage/icons/operational_optimization.svg';

import HardcodedSalesDemoPath from './hardcodedSalesDemoPath';

const StyledLink = styled(Link)`
  color: rgb(31, 169, 224);
  :hover {
    text-decoration: underline;
  }
`;

export interface ICloudEncodingNoteworthyFeature {
  readonly name: string;
  readonly description?: JSX.Element;
  readonly bullets: JSX.Element[];
  readonly iconPath: string;
}

const superResolutionRef = AppRoutePathFunction.DemoDetail({demoId: 'e7a22379-bbbf-11ec-a62d-42010a8400cb'});
const infoGraphic = '//sales-demos-content.bitmovin.com/foster/Bitmovin_Encoder_Infographic.pdf';
const fullReport = '//sales-demos-content.bitmovin.com/foster/TEI_of_Bitmovin_Encoder_final_FINAL110121.pdf';
const EncodingNoteworthyFeatureData: ReadonlyArray<ICloudEncodingNoteworthyFeature> = [
  {
    name: 'High Quality',
    bullets: [
      <>
        Improve the quality with{' '}
        <StyledLink to={AppRoutePathFunction.DemoDetail({demoId: 'b57de324-980a-11e9-b128-42010a840215'})}>
          Per-Title encoding
        </StyledLink>
        . It automatically analyzes each video and creates the ideal adaptive bitrate ladder (ABR).
      </>, // tslint:disable-line:max-line-length
      <>
        Our{' '}
        <StyledLink to={AppRoutePathFunction.DemoDetail({demoId: 'b57de374-980a-11e9-b128-42010a840215'})}>
          multi-codec streaming
        </StyledLink>{' '}
        approach matches codecs to browsers dynamically with 50% bandwidth savings or more and delivers the best quality
        possible based on the viewers’ browser.
      </>,
      <>
        Cloud implementations of AV1 and{' '}
        <StyledLink to={AppRoutePathFunction.DemoDetail({demoId: '91617c67-96df-44dd-911e-84babf62f12d'})}>
          VVC
        </StyledLink>{' '}
        codecs are making 4K and HDR video possible at lower bandwidths (>700kbps!).
      </>,
      <>
        Besides supporting multiple{' '}
        <StyledLink to={AppRoutePathFunction.DemoDetail({demoId: 'b5af068d-980a-11e9-b128-42010a840215'})}>
          HDR
        </StyledLink>{' '}
        and immersive audio formats, we are the first Dolby Pro Partner to integrate Dolby Vision and Dolby Atmos in the
        cloud.
      </>
    ],
    iconPath: ExceptionalViewerExperienceSvg
  },
  {
    name: 'Ease of use',
    bullets: [
      <>Quality focused features such as Per-Title, HDR, multi-pass, AV1 are implemented in minutes.'</>,
      <>
        Encoding jobs can run on AWS, GCP, or Azure within Bitmovin’s managed cloud or on your own cloud resources.
        Spikes in demand and large batches of content run quickly and efficiently in the cloud. And you pay only for
        resources used.
      </>,
      <>
        Forrester’s Total Economic Impact™ Study found that Bitmovin’s Encoding solution delivered a 355% ROI over the
        course of three years. Offering the optimal balance between quality, performance, and cost. Checkout the{' '}
        <a href={infoGraphic} target={'_blank'}>
          key findings
        </a>{' '}
        and{' '}
        <a href={fullReport} target={'_blank'}>
          full report
        </a>
        .
      </>
    ],
    iconPath: OperationalOptimizationSvg
  },
  {
    name: 'Always innovative',
    bullets: [
      <>
        Per-Title Encoding +{' '}
        <StyledLink to={AppRoutePathFunction.DemoDetail({demoId: 'b57de0aa-980a-11e9-b128-42010a840215'})}>
          3-Pass
        </StyledLink>{' '}
        Encoding = Best Quality of Experience (QoE) for viewers and lower Total Cost of Ownership (TCO) by 30%. Now
        possible with one simple API call.
      </>,
      <>
        <StyledLink to={AppRoutePathFunction.DemoDetail({demoId: 'de9f4a2e-ab3a-4333-8f45-1028fe3320d2'})}>
          Per-Shot Encoding
        </StyledLink>{' '}
        takes it to the next level. Beta tests indicated up to 20% savings when using Per-Title and Per-Shot encoding
        compared to using traditional fixed segment lengths.
      </>,
      <>
        Experience <StyledLink to={superResolutionRef}>Super-Resolution</StyledLink> with machine-learning based
        upscaling that prepares and boosts the quality of SD content for 4K screens.
      </>
    ],
    iconPath: FasterTimeToMarketSvg
  }
];

export default EncodingNoteworthyFeatureData;
